import type { PartitionPack } from './partitionPack';
import { parsePartitionPack } from './partitionPack';
import type { KLV } from './klv';
import { MxfDictionary, SMPTE_UL_KLV_FILLS, SMPTE_UL_PARTITION_PACKS, SMPTE_UL_PRIMER_PACK, isStructuralMetadataSet } from './mxfDictionary';
import cloneDeep from 'lodash/cloneDeep';
import type { OpenResource } from '../../io/resource';

export interface PartitionPackAndHeaders {
  partitionPack: PartitionPack,
  headers: KLV[],
}

export function failOnSecondEncounteredNonFooterPartition(): TransformStream<PartitionPackAndHeaders, PartitionPackAndHeaders> {
  let nonFooterPartitionsEncountered = 0;
  return new TransformStream<PartitionPackAndHeaders, PartitionPackAndHeaders>({
    transform: (partitionPackAndHeaders: PartitionPackAndHeaders, controller) => {
      if (partitionPackAndHeaders.partitionPack.partitionType !== 'footer') {
        nonFooterPartitionsEncountered ++;
        if (nonFooterPartitionsEncountered >= 2) {
          controller.error(new Error('Encountered more than one non-footer partitions'));
        }
      } else {
        controller.enqueue(partitionPackAndHeaders);
      }
    }
  });
}

export function readPartitionPackAndHeaders(): TransformStream<KLV, PartitionPackAndHeaders> {
  let partitionPack : PartitionPack | null = null;
  const headers : KLV[] = [];
  let inHeaders = false;
  const dictionary = new MxfDictionary();
  return new TransformStream<KLV, PartitionPackAndHeaders>({
    transform: (klv: KLV, controller) => {
      if (SMPTE_UL_PARTITION_PACKS.has(klv.key)) {
        if (partitionPack !== null) {
          // This means we've encountered the partition pack of the next partition which means
          // we've read all the headers
          controller.enqueue({ partitionPack, headers });
          partitionPack = null;
          inHeaders = false;
        }
        partitionPack = parsePartitionPack(klv);
        inHeaders = true;
      } else if (!inHeaders) {
        // Ignore all KLVs after headers
      } else if ((dictionary.getName(klv.key) === undefined) || SMPTE_UL_KLV_FILLS.has(klv.key)) {
        if (headers.length > 0) {
          headers.push(klv);
        }
      } else if (isStructuralMetadataSet(klv.key) || (klv.key === SMPTE_UL_PRIMER_PACK)) {
        headers.push(klv);
      } else {
        inHeaders = false;
      }
    },
    flush: (controller) => {
      if (partitionPack !== null) {
        controller.enqueue({ partitionPack, headers });
      }
    }
  });
}

export function deriveFooterFromHeader(partitionPackAndHeader: PartitionPackAndHeaders): PartitionPackAndHeaders {
  return {
    partitionPack: {
      ...partitionPackAndHeader.partitionPack,
      partitionType: 'footer',
      closed: true,
      complete: true,
    },
    headers: cloneDeep(partitionPackAndHeader.headers),
  };
}

export async function completeTransform<T>(
  openResource: OpenResource,
  what: string,
  convert: (readable: ReadableStream<Uint8Array>) => ReadableStream<T>
): Promise<T> {
  const reader = convert(openResource.stream).getReader();
  const result = await reader.read();
  if (!result.value) {
    throw new Error(`Could not parse ${what}.`);
  }
  return result.value;
}

